import { MAX_MAP_PLOT_REQUEST_DETAILS } from "@/constants/mapPlotRequest";
import { useState } from "react";
import type React from "react";
import { toast } from "react-toastify";
import { isAxiosError, HttpStatusCode } from "axios";
import { useApiClient } from "@/hooks/useApiClient";
import { readFile } from "@/utils/readFile";

export const MAX_CSV_ROWS = MAX_MAP_PLOT_REQUEST_DETAILS;

interface UseCsvUploadProps {
  onSuccess?: () => void;
  onClose?: () => void;
  setCsvUploadLoading?: (loading: boolean) => void;
}

interface UseCsvUploadReturn {
  fileToCsvUpload: File | null;
  showFileErrorMessage: boolean;
  csvUploadLoading: boolean;
  handleFileChange: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  uploadCsv: (name: string, tags?: string[], mapPlotRequestId?: number) => void;
  resetCsvUpload: () => void;
}

export const useCsvUpload = ({
  onSuccess,
  onClose,
  setCsvUploadLoading,
}: UseCsvUploadProps = {}): UseCsvUploadReturn => {
  const [fileToCsvUpload, setFileToCsvUpload] = useState<File | null>(null);
  const [showFileErrorMessage, setShowFileErrorMessage] =
    useState<boolean>(false);
  const [csvUploadLoading, setLocalCsvUploadLoading] = useState<boolean>(false);
  const { apiClient } = useApiClient();

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    setShowFileErrorMessage(false);
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      try {
        const fileData = await readFile(file);
        if (fileData.data.length > MAX_CSV_ROWS) {
          setShowFileErrorMessage(true);
          setFileToCsvUpload(null);
          return;
        }
        setFileToCsvUpload(file);
      } catch (error) {
        console.error(error);
        toast.error("ファイルの読み込みに失敗しました");
        setFileToCsvUpload(null);
      }
    }
  };

  const uploadCsv = (
    name: string,
    tags?: string[],
    mapPlotRequestId?: number
  ): void => {
    if (!fileToCsvUpload) return;

    setLocalCsvUploadLoading(true);
    if (setCsvUploadLoading) {
      setCsvUploadLoading(true);
    }

    const formData = new FormData();
    formData.append("file", fileToCsvUpload);
    formData.append("name", name);

    if (tags && tags.length > 0) {
      tags.forEach((tag) => {
        formData.append("tags", tag);
      });
    }

    if (mapPlotRequestId !== undefined) {
      formData.append("mapPlotRequestId", mapPlotRequestId.toString());
    }

    apiClient
      .post("/map/file?type=csv", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        if (onSuccess) {
          onSuccess();
        }
        setLocalCsvUploadLoading(false);
        if (setCsvUploadLoading) {
          setCsvUploadLoading(false);
        }
        if (onClose) {
          onClose();
        }
      })
      .catch((error: Error) => {
        setLocalCsvUploadLoading(false);
        if (setCsvUploadLoading) {
          setCsvUploadLoading(false);
        }
        console.error(error);
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.BadRequest
        ) {
          let errorMessage = "ファイルのアップロードに失敗しました";
          if (isAxiosError(error) && error.response?.data) {
            const errorData: unknown = error.response.data;
            if (Array.isArray(errorData) && errorData.length > 0) {
              errorMessage = String(errorData[0]);
            }
          }
          toast.error(errorMessage);
        } else {
          toast.error("ファイルのアップロードに失敗しました");
        }
      });
  };

  const resetCsvUpload = (): void => {
    setFileToCsvUpload(null);
    setShowFileErrorMessage(false);
  };

  return {
    fileToCsvUpload,
    showFileErrorMessage,
    csvUploadLoading,
    handleFileChange,
    uploadCsv,
    resetCsvUpload,
  };
};
